import { useGetPracticeSeedings, useGetPracticeTillages } from "utils";

import { FieldPracticeResponse, PRACTICE_SLUG, PracticeFCPO } from "models";

/**
 * Custom hook that provides helper functions for tillage and seeding practices
 * Encapsulates data fetching and business logic to avoid prop drilling
 */
export const useTillageAndSeedingHelpers = () => {
  const { data: tillages } = useGetPracticeTillages({ enabled: true });
  const { data: seedings } = useGetPracticeSeedings({ enabled: true });

  // Helper function to get tillage/seeding name
  const getPracticeName = (type: string, assignedPracticeId: string, fallbackText = "Unknown") => {
    const practiceList = type === PRACTICE_SLUG.TILLAGES ? tillages : seedings;
    return practiceList?.find((p) => p.id === assignedPracticeId)?.name ?? fallbackText;
  };

  // Helper function to get diesel usage
  const getDieselUsage = (practice: { type: string; assignedPracticeId: string }) => {
    if (practice.type === PRACTICE_SLUG.TILLAGES) {
      const tillage = tillages?.find((t) => t.id === practice.assignedPracticeId);
      return tillage?.dieselUsage ?? null;
    }

    if (practice.type === PRACTICE_SLUG.SEEDINGS) {
      const seeding = seedings?.find((s) => s.id === practice.assignedPracticeId);
      return seeding?.dieselUsage ?? null;
    }

    return null;
  };

  // Transform crops to seeding practices
  const transformCropsToSeedingPractices = (crops: FieldPracticeResponse["crops"] = []) =>
    crops
      .filter((crop) => crop.seedingPracticeId)
      .map((crop) => ({
        id: crop.id, // Use crop ID for uniqueness
        seedingPracticeId: crop.seedingPracticeId, // Store seeding practice ID for API calls
        type: PRACTICE_SLUG.SEEDINGS as typeof PRACTICE_SLUG.SEEDINGS,
        assignedPracticeId: crop.seedingPracticeType!,
        year: crop.year,
        endDate: crop.seedingStartDate || crop.harvestStartDate || "",
        startDate: crop.seedingStartDate,
        isConfirmed: true,
        verificationStatus: crop.seedingVerificationStatus,
        cropId: crop.id,
      }));

  // Combine tillage and seeding practices
  const getCombinedPractices = (fieldPractices?: FieldPracticeResponse): PracticeFCPO[] => {
    const tillagePractices = fieldPractices?.practices?.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES) ?? [];
    const seedingPractices = transformCropsToSeedingPractices(fieldPractices?.crops);
    return [...tillagePractices, ...seedingPractices] as PracticeFCPO[];
  };

  // Sort practices by endDate (most recent first)
  const sortPracticesByEndDate = (practices: PracticeFCPO[]) =>
    practices.sort((a, b) => {
      if (!a.endDate) return 1;
      if (!b.endDate) return -1;
      return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
    });

  // Get practice icon name
  const getPracticeIconName = (practiceType: string) => (practiceType === PRACTICE_SLUG.TILLAGES ? "klim/tillage" : "klim/planting-seeds");

  return {
    getPracticeName,
    getDieselUsage,
    getCombinedPractices,
    sortPracticesByEndDate,
    getPracticeIconName,
  };
};
